import { useEffect, useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { LogOut, LayoutGrid, UserPlus, Users, FileText, Image as ImageIcon, BarChart3, Settings, MapPin, Megaphone } from 'lucide-react';
import { adminApi, clearToken, getToken } from '../lib/adminApi';

const NAV = [
  { to: '/admin/applications', label: 'New Artists', icon: UserPlus, badge: true },
  { to: '/admin', label: 'Artists', icon: Users, exact: true },
  { to: '/admin/artist-form', label: 'Artist Form', icon: FileText },
  { to: '/admin/locations', label: 'Locations', icon: MapPin },
  { to: '/admin/announcements', label: 'Announcements', icon: Megaphone },
  { to: '/admin/home-settings', label: 'Home & Browse', icon: LayoutGrid },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/site-images', label: 'Site Images', icon: ImageIcon },
];

export default function AdminLayout() {
  const [authed, setAuthed] = useState(null);
  const [pending, setPending] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminApi.me().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const refreshBadge = () => adminApi.applicationsCount().then(d => setPending(d.pending || 0)).catch(() => {});
  useEffect(() => {
    if (authed !== true) return;
    refreshBadge();
    const t = setInterval(refreshBadge, 30000);
    return () => clearInterval(t);
  }, [authed, pathname]);

  if (authed === null) return <div className="min-h-screen grid place-items-center bg-white text-sm uppercase tracking-[0.25em] text-neutral-500">Loading…</div>;
  if (!authed) return <Navigate to="/admin/login" replace />;

  const logout = () => { clearToken(); window.location.href = '/admin/login'; };
  const isActive = (item) => item.exact ? pathname === item.to : pathname.startsWith(item.to);

  return (
    <div className="bg-[#fafafa] min-h-screen flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-white border-r border-[#eaeaea] min-h-screen hidden md:flex flex-col fixed">
        <Link to="/admin" className="h-16 flex items-center px-6 font-serif text-xl border-b border-[#eaeaea]">
          FindYour<span className="text-[#eb5381]">MUA</span>
        </Link>
        <nav className="flex-1 py-4">
          {NAV.map(item => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-6 py-3 text-[11px] uppercase tracking-[0.18em] transition-colors ${isActive(item) ? 'text-[#eb5381] bg-[#fff5f8] border-r-2 border-[#eb5381]' : 'text-neutral-600 hover:text-[#eb5381]'}`}>
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.badge && pending > 0 && (
                  <span data-testid="new-artists-badge" className="bg-[#eb5381] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">{pending}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-[#eaeaea] space-y-2">
          <Link to="/" className="flex items-center gap-2 px-2 py-2 text-[11px] uppercase tracking-[0.18em] text-neutral-600 hover:text-[#eb5381]">View Site ↗</Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 px-2 py-2 text-[11px] uppercase tracking-[0.18em] text-neutral-600 hover:text-[#eb5381]"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-white border-b border-[#eaeaea]">
        <div className="flex items-center gap-3 overflow-x-auto px-4 h-14 text-[10px] uppercase tracking-[0.15em]">
          {NAV.map(item => (
            <Link key={item.to} to={item.to} className={`whitespace-nowrap relative ${isActive(item) ? 'text-[#eb5381]' : 'text-neutral-600'}`}>
              {item.label}{item.badge && pending > 0 && <span className="ml-1 bg-[#eb5381] text-white rounded-full px-1.5">{pending}</span>}
            </Link>
          ))}
          <button onClick={logout} className="whitespace-nowrap text-neutral-600">Logout</button>
        </div>
      </div>

      <main className="flex-1 md:ml-60 px-5 md:px-10 py-8 md:py-10 mt-14 md:mt-0">
        <Outlet />
      </main>
    </div>
  );
}
